import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated, selectRoles, selectAftersalesRoles, selectIsAftersalesUser, selectManageRoles } from '../../features/auth/authSlice';
import { selectUserParams } from '../../store/slices/userParamsSlice';
import { permissionChecker } from '../../utils/permissionChecker';
import { usePermissionGrants } from '../../hooks/usePermissionGrants';
import { ProgressSpinner } from 'primereact/progressspinner';

/**
 * Route seviyesinde yetki kontrolü yapan bileşen
 * @param {Object} props - Bileşen props'ları
 * @param {React.ReactNode} props.children - Render edilecek içerik
 * @param {Object} props.permissions - Gerekli yetkiler
 * @param {string[]} props.permissions.roles - Gerekli roller
 * @param {string[]} props.permissions.aftersalesRoles - Gerekli aftersales roller
 * @param {boolean} props.permissions.requiresWarrantyTeam - WARRANTY_TEAM rolü gerekli mi
 * @param {boolean} props.permissions.requiresKmobilityUser - Kmobility kullanıcısı gerekli mi
 * @param {boolean} props.permissions.requiresKarsanLogistics - KARSAN_LOGISTICS rolü gerekli mi
 * @param {boolean} props.permissions.requiresSalesDealer - SALES_DEALER rolü gerekli mi (yedek parça modülü için)
 * @param {boolean} props.permissions.requiresPartTeam - PART_TEAM rolü gerekli mi (yedek parça ve duyurular modülü için)
 * @param {boolean} props.permissions.requiresWarrantyQualityOfficer - WARRANTY_QUALITY_OFFICER rolü gerekli mi (teknik destek talep formu için)
 * @param {boolean} props.permissions.requiresAdminOnly - Sadece ADMIN/RegionalManager rolü gerekli mi (diğer super user'lar erişemez, OR mantığına dahil değil)
 * @param {boolean} props.permissions.requiresAdmin - ADMIN/RegionalManager rolü gerekli mi (OR mantığına dahil, diğer yetkilerle birlikte kullanılabilir)
 * @param {boolean} props.permissions.requiresServiceBulletinUser - Servis bülteni görüntüleme rolü gerekli mi
 * @param {boolean} props.permissions.requiresAftersalesUser - Oturumdaki aftersales kullanıcı bayrağı (auth) gerekli mi
 * @param {string[]} props.permissions.anyPermission - Yetki kodları (biri yeterli)
 * @param {Object} props.permissions.orLegacy - Geçiş dönemi: anyPermission yoksa uygulanan eski kural
 * @param {string} props.redirectTo - Yetkisiz erişimde yönlendirilecek sayfa
 */
const ProtectedRoute = ({ children, permissions = null, redirectTo = '/login' }) => {
    const isAuthenticated = useSelector(selectIsAuthenticated);
    const userRoles = useSelector(selectRoles);
    const aftersalesRoles = useSelector(selectAftersalesRoles);
    const isAftersalesUser = useSelector(selectIsAftersalesUser);
    const userParams = useSelector(selectUserParams);
    const manageRoles = useSelector(selectManageRoles);
    const grants = usePermissionGrants();
    const location = useLocation();

    // Eğer kullanıcı giriş yapmamışsa login sayfasına yönlendir
    if (!isAuthenticated) {
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // Eğer yetki kontrolü yapılmayacaksa direkt içeriği render et
    if (!permissions) {
        return <>{children}</>;
    }

    // Permission checker ile yetki kontrolü
    const userData = {
        roles: userRoles,
        aftersalesRoles: aftersalesRoles,
        userParams: userParams,
        isAftersalesUser,
        manageRoles,
        grants
    };

    const permissionResult = permissionChecker.checkPermissions(userData, permissions);

    // Yetkiler yüklenmeden /403'e atma (F5'te yetkili kullanıcı bir anlığına atılmasın)
    if (permissionResult.pending) {
        return (
            <div className="flex justify-content-center align-items-center p-6">
                <ProgressSpinner style={{ width: '3rem', height: '3rem' }} />
            </div>
        );
    }

    if (!permissionResult.hasAccess) {
        return <Navigate to="/403" state={{ from: location }} replace />;
    }

    // Tüm kontroller geçildi, içeriği render et
    return <>{children}</>;
};

export default ProtectedRoute;
